import React, { useState } from 'react';
import styled from 'styled-components';
import BalanceIcon from '@mui/icons-material/Balance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AgentProgressCard, { AgentPhase } from './AgentProgressCard';

interface PanoramaProgressProps {
  phase: string;
  progress: number;
  nSearches?: number;
  nDocs?: number;
  nTheses?: number;
  inputTokens?: number;
  outputTokens?: number;
  startedAt?: number;
  nWebSearches?: number;
}

// Stepper do card (mesma ordem do orquestrador do panorama).
const PHASES: AgentPhase[] = [
  { key: 'planning', label: 'Análise do caso' },
  { key: 'researching', label: 'Pesquisa' },
  { key: 'reviewing', label: 'Conferência do caderno' },
  { key: 'synthesizing', label: 'Memorando' },
];

// Fases transientes do backend → posição no stepper.
const PHASE_ALIAS: Record<string, string> = {
  phase1: 'planning',
  phase1_done: 'researching',
  workers: 'researching',
  citations: 'reviewing',
  forward: 'reviewing',
  dedup: 'reviewing',
  export: 'synthesizing',
  planner: 'synthesizing',
};

const formatTokenCount = (n: number): string => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toString();
};

const PanoramaProgress: React.FC<PanoramaProgressProps> = ({
  phase,
  progress,
  nSearches = 0,
  nDocs = 0,
  nTheses = 0,
  inputTokens = 0,
  outputTokens = 0,
  startedAt,
  nWebSearches,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isDone = phase === 'done';
  const isError = phase === 'error';
  const isInterrupted = phase === 'interrupted';
  const activeKey = PHASE_ALIAS[phase] || phase;
  const activeIndex = Math.max(0, PHASES.findIndex((p) => p.key === activeKey));
  const hasStats = nSearches > 0 || (nWebSearches || 0) > 0 || nDocs > 0 || nTheses > 0 || inputTokens > 0 || outputTokens > 0;

  const title = isError
    ? 'Falha no panorama jurídico'
    : isInterrupted
    ? 'Panorama jurídico interrompido'
    : isDone
    ? 'Panorama jurídico concluído'
    : 'Montando o panorama jurídico';

  return (
    <AgentProgressCard
      icon={isDone
        ? <CheckCircleIcon style={{ fontSize: 17, color: '#047857' }} />
        : <BalanceIcon style={{ fontSize: 17 }} />}
      title={title}
      phases={PHASES}
      activeIndex={isDone ? PHASES.length : activeIndex}
      progress={progress}
      done={isDone}
      error={isError || isInterrupted}
      startedAt={startedAt}
      onHeaderClick={hasStats ? () => setExpanded((v) => !v) : undefined}
      headerExtra={hasStats ? (
        <Stats>
          {nSearches > 0 && <span aria-label={`${nSearches} buscas no total`} title="Buscas no total"><SearchIcon style={{ fontSize: 12 }} /> {nSearches}</span>}
          {nWebSearches !== undefined && <span aria-label={`${nWebSearches} buscas web`} title="Buscas web (incluídas no total)"><PublicIcon style={{ fontSize: 12 }} /> {nWebSearches} web</span>}
          {nDocs > 0 && <span><DescriptionIcon style={{ fontSize: 12 }} /> {nDocs}</span>}
          {nTheses > 0 && <span aria-label={`${nTheses} referências`}><GavelIcon style={{ fontSize: 12 }} /> {nTheses}</span>}
          {(inputTokens > 0 || outputTokens > 0) && (
            <>
              <span>↑ {formatTokenCount(inputTokens)}</span>
              <span>↓ {formatTokenCount(outputTokens)}</span>
            </>
          )}
          {expanded ? <ExpandLessIcon style={{ fontSize: 15 }} /> : <ExpandMoreIcon style={{ fontSize: 15 }} />}
        </Stats>
      ) : undefined}
    >
      {expanded && (
        <Details>
          <DetailLine>
            <DetailIcon><SearchIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{nSearches}</strong> buscas no total</DetailLabel>
            <DetailDesc>inclui bases jurídicas e web</DetailDesc>
          </DetailLine>
          {nWebSearches !== undefined && <DetailLine>
            <DetailIcon><PublicIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{nWebSearches}</strong> buscas web</DetailLabel>
            <DetailDesc>consultas na web, incluindo a pesquisa paralela</DetailDesc>
          </DetailLine>}
          <DetailLine>
            <DetailIcon><DescriptionIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{nDocs}</strong> documentos</DetailLabel>
            <DetailDesc>acórdãos e normas recuperados e lidos</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon><GavelIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{nTheses}</strong> referências</DetailLabel>
            <DetailDesc>referências reunidas na pesquisa</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon>↑</DetailIcon>
            <DetailLabel><strong>{formatTokenCount(inputTokens)}</strong> tokens de entrada</DetailLabel>
            <DetailDesc>texto enviado ao modelo durante toda a pesquisa</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon>↓</DetailIcon>
            <DetailLabel><strong>{formatTokenCount(outputTokens)}</strong> tokens de saída</DetailLabel>
            <DetailDesc>texto gerado pelo modelo durante a pesquisa</DetailDesc>
          </DetailLine>
        </Details>
      )}
    </AgentProgressCard>
  );
};

export default PanoramaProgress;

const Stats = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  opacity: 0.7;
  font-size: 0.78rem;
  & > span { display: inline-flex; align-items: center; gap: 3px; }
`;

const Details = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.8rem;
  opacity: 0.85;
`;

const DetailLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 7px;
`;

const DetailIcon = styled.span`
  display: inline-flex;
  align-items: center;
  width: 16px;
  justify-content: center;
  opacity: 0.7;
`;

const DetailLabel = styled.span`
  white-space: nowrap;
`;

const DetailDesc = styled.span`
  opacity: 0.6;
`;
